import {Component, computed, DestroyRef, effect, HostListener, inject, input, OnInit, signal} from '@angular/core';
import {ElciaCard} from '../../../../components/card/card';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {PartyDropdown} from '../../../../components/party-dropdown/party-dropdown';
import {CompanyService, ContactService, CountryService, PartySelect, PartyType, VatZone} from '../../../../../api';
import {PartyResource} from '../../../../../api/resources';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {TaxStore} from '../../services/tax-store';
import {QueryKey} from '../../../../constants/query-key';
import {CurrentUserService} from '../../../../services/current-user.service';
import {ElciaDatePicker} from '../../../../components/date-picker/date-picker';
import {InvoiceLineForm} from '../invoice-line-form/invoice-line-form';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {Divider} from 'primeng/divider';
import {AmountRecap, InvoiceLineAmount} from '../tax-recap/amount-recap.component';
import {takeUntilDestroyed, toObservable, toSignal} from '@angular/core/rxjs-interop';
import {Editor} from '../../../../components/editor/editor';
import {LegalEntityDropdown} from '../legal-entity-dropdown/legal-entity-dropdown';
import {LocalDropdown} from '../../../../components/local-dropdown/local-dropdown';
import {InvoiceLineModelForm, InvoiceModelForm} from '../invoice-form/invoice-form';
import {startWith, switchMap} from 'rxjs';

const BELGIUM_ISO_CODE = 'BE';

@Component({
  selector: 'elcia-invoice-edit',
  imports: [
    ElciaCard,
    TranslatePipe,
    PartyDropdown,
    ElciaDatePicker,
    InvoiceLineForm,
    ElciaButton,
    Divider,
    AmountRecap,
    Editor,
    LegalEntityDropdown,
    LocalDropdown
  ],
  templateUrl: './invoice-edit.html',
  styleUrl: './invoice-edit.scss'
})
export class InvoiceEdit implements OnInit {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly partyResource = inject(PartyResource);
  private readonly companyService = inject(CompanyService);
  private readonly contactService = inject(ContactService);
  private readonly countryService = inject(CountryService);
  private readonly taxStore = inject(TaxStore);

  // Lie chaque ligne de prestation à sa ligne de frais de déplacement dédiée, pour la mettre à jour ou la retirer.
  private readonly travelLineBySource = new Map<FormGroup<InvoiceLineModelForm>, FormGroup<InvoiceLineModelForm>>();

  readonly fg = input.required<FormGroup<InvoiceModelForm>>();
  readonly minInvoiceDate = input<Date | null>(null);
  private readonly fg$ = toObservable(this.fg);

  protected readonly partyTypes = signal<PartyType[]>([PartyType.Contact, PartyType.Company]);

  protected readonly legalEntityIdValue = this.controlValue(fg => fg.controls.legalEntityId);
  protected readonly customerIdValue = this.controlValue(fg => fg.controls.customerId);
  protected readonly invoiceDateValue = this.controlValue(fg => fg.controls.invoiceDate);

  protected readonly canEditLines = computed(() =>
    this.legalEntityIdValue() != null && this.customerIdValue() != null,
  );

  // Resolves the selected customer's VAT zone to drive the mandatory tax on foreign invoices.
  // customerId -> party (for its type) -> company/contact details (for the country) -> country VAT zone.
  private readonly partiesResource = this.partyResource.getParties(this.partyTypes);
  private readonly customerParty = computed(() =>
    this.partiesResource.value()?.parties.find(p => p.id === this.customerIdValue()) ?? null,
  );

  private readonly companyDetailsQuery = injectQuery(() => {
    const party = this.customerParty();
    const companyId = party?.type === PartyType.Company ? party.id : null;
    return {
      queryKey: [QueryKey.COMPANY, companyId],
      queryFn: () => lastValueFrom(this.companyService.getCompanyById(companyId!)),
      enabled: companyId != null,
    };
  });

  private readonly contactDetailsQuery = injectQuery(() => {
    const party = this.customerParty();
    const contactId = party?.type === PartyType.Contact ? party.id : null;
    return {
      queryKey: [QueryKey.CONTACT, contactId],
      queryFn: () => lastValueFrom(this.contactService.getContactById(contactId!)),
      enabled: contactId != null,
    };
  });

  private readonly customerCountryIsoCode = computed<string | null>(() => {
    const party = this.customerParty();
    if (party?.type === PartyType.Company)
      return this.companyDetailsQuery.data()?.company.address?.countryIsoCode ?? null;
    if (party?.type === PartyType.Contact)
      return this.contactDetailsQuery.data()?.contact.address?.countryIsoCode ?? null;
    return null;
  });

  // The country is only fetched for non-Belgian customers, to read its VAT zone.
  private readonly countryDetailsQuery = injectQuery(() => {
    const isoCode = this.customerCountryIsoCode();
    const isForeign = isoCode != null && isoCode !== BELGIUM_ISO_CODE;
    return {
      queryKey: [QueryKey.COUNTRY_DETAILS, isoCode],
      queryFn: () => lastValueFrom(this.countryService.getCountryDetails(isoCode!)),
      enabled: isForeign,
    };
  });

  private readonly customerVatZone = computed<VatZone | null>(() => {
    const isoCode = this.customerCountryIsoCode();
    if (isoCode == null) return null;
    if (isoCode === BELGIUM_ISO_CODE) return VatZone.Domestic;
    return this.countryDetailsQuery.data()?.country.vatZone ?? null;
  });

  // Foreign customers get a mandatory tax; Belgian ones (or unresolved) keep a free choice (null).
  protected readonly forcedTaxId = computed<number | null>(() => {
    switch (this.customerVatZone()) {
      case VatZone.IntraCommunity:
        return this.taxStore.intraCommunityTax()?.id ?? null;
      case VatZone.Export:
        return this.taxStore.exportTax()?.id ?? null;
      default:
        return null;
    }
  });

  private readonly invoiceLinesValue = toSignal(
    this.fg$.pipe(switchMap(fg =>
      fg.controls.invoiceLines.valueChanges.pipe(startWith(fg.controls.invoiceLines.value)),
    )),
  );
  // getRawValue is used so a locked (disabled) taxId is still counted in the VAT recap.
  protected readonly invoiceLineRecap = computed(() => {
    this.invoiceLinesValue();
    this.forcedTaxId();
    return this.fg().controls.invoiceLines.getRawValue().map(line => ({
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      taxId: line.taxId,
    } as InvoiceLineAmount));
  });

  // Enforces the mandatory tax on every line (existing and newly added) and locks the field for
  // foreign customers; unlocks and resets to the default tax once the customer is Belgian again.
  private readonly enforceTaxLock = effect(() => {
    const forcedTaxId = this.forcedTaxId();
    this.invoiceLinesValue(); // re-run whenever a line is added or removed

    for (const line of this.fg().controls.invoiceLines.controls) {
      const taxControl = line.controls.taxId;
      if (forcedTaxId != null) {
        if (taxControl.value !== forcedTaxId) taxControl.setValue(forcedTaxId, {emitEvent: false});
        if (taxControl.enabled) taxControl.disable({emitEvent: false});
      } else if (taxControl.disabled) {
        taxControl.enable({emitEvent: false});
        const defaultTaxId = this.taxStore.defaultTax()?.id ?? null;
        if (defaultTaxId != null) taxControl.setValue(defaultTaxId, {emitEvent: false});
      }
    }
  });

  ngOnInit() {
    if (this.fg().controls.invoiceLines.length === 0) {
      this.addInvoiceLine();
    }
  }

  protected onCustomerSelected(party: PartySelect) {
    const locale = party.isoLocale ?? this.currentUserService.currentUser()?.localeIsoCode ?? null;
    this.fg().controls.localeIsoCode.setValue(locale);
  }

  @HostListener('window:keydown.alt.+')
  protected addInvoiceLine() {
    this.fg().controls.invoiceLines.push(this.createInvoiceLineForm());
    // push() ne salit pas le formulaire : sans ça, un rechargement des données effacerait la ligne ajoutée.
    this.fg().controls.invoiceLines.markAsDirty();
  }

  protected removeInvoiceLine(index: number) {
    const lines = this.fg().controls.invoiceLines;
    const removed = lines.at(index);
    lines.markAsDirty();

    // Une prestation supprimée emporte sa ligne de frais de déplacement.
    const travelLine = this.travelLineBySource.get(removed);
    if (travelLine) {
      const travelIndex = lines.controls.indexOf(travelLine);
      if (travelIndex !== -1) {
        lines.removeAt(travelIndex);
      }
      this.travelLineBySource.delete(removed);
    }

    const currentIndex = lines.controls.indexOf(removed);
    if (currentIndex !== -1) {
      lines.removeAt(currentIndex);
    }
  }

  // Crée, met à jour ou retire la ligne « Frais de déplacement » associée à une prestation.
  // UnitPrice = somme des frais ; Quantity = 1 ; Produit et TVA alignés sur la prestation.
  protected onTravelExpensesResolved(sourceLine: FormGroup<InvoiceLineModelForm>, amount: number) {
    const lines = this.fg().controls.invoiceLines;
    const tracked = this.travelLineBySource.get(sourceLine);
    // La ligne de frais a pu être supprimée à la main : on ignore une référence obsolète.
    const existing = tracked && lines.controls.includes(tracked) ? tracked : undefined;
    if (tracked && !existing) {
      this.travelLineBySource.delete(sourceLine);
    }

    if (amount <= 0) {
      if (existing) {
        lines.removeAt(lines.controls.indexOf(existing));
        this.travelLineBySource.delete(sourceLine);
      }
      return;
    }

    if (existing) {
      existing.controls.unitPrice.setValue(amount);
      return;
    }

    const travelLine = this.createInvoiceLineForm();
    travelLine.patchValue({
      label: this.translate.instant('invoice.travel-expenses'),
      quantity: 1,
      unitPrice: amount,
      productId: sourceLine.controls.productId.value,
      taxId: sourceLine.controls.taxId.value,
      isTravelExpenses: true,
    });
    // Produit verrouillé : il reflète la prestation source, non modifiable sur la ligne de frais.
    travelLine.controls.productId.disable();

    const sourceIndex = lines.controls.indexOf(sourceLine);
    lines.insert(sourceIndex + 1, travelLine);
    this.travelLineBySource.set(sourceLine, travelLine);

    // La ligne de frais suit la TVA de la prestation, même si elle est choisie après les prestations.
    sourceLine.controls.taxId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(taxId => travelLine.controls.taxId.setValue(taxId));
  }

  private createInvoiceLineForm() {
    return new FormGroup<InvoiceLineModelForm>({
      id: new FormControl(null),
      productId: new FormControl<number | null>(null, Validators.required),
      timesheetIds: new FormControl<number[]>([], {nonNullable: true}),
      label: new FormControl(null, Validators.required),
      description: new FormControl(null),
      quantity: new FormControl(null, Validators.required),
      unitPrice: new FormControl(null, Validators.required),
      taxId: new FormControl(null, Validators.required),
      isTravelExpenses: new FormControl(false, {nonNullable: true}),
      customerProductBillingId: new FormControl<number | null>(null),
      billingPeriodStart: new FormControl<Date | null>(null),
      billingPeriodEnd: new FormControl<Date | null>(null),
    });
  }

  private controlValue<T>(selector: (fg: FormGroup<InvoiceModelForm>) => FormControl<T>) {
    return toSignal(
      this.fg$.pipe(switchMap(fg => {
        const control = selector(fg);
        return control.valueChanges.pipe(startWith(control.value));
      })),
      {initialValue: null},
    );
  }
}
